import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useContext } from 'react';
import dayjs from 'dayjs';
import { fetchingTeachersForStudents } from '../../../api/teachers';
import { usePreRegisteredUsers } from '../../../contexts/userDataContext';
import AuthContext from '../../../contexts/AuthContext';
import { getUserSubRoleName, toIsoString } from '../utils/clinicalSessionHelpers';
import { smartJsonAtob } from '../../../utils/encodingUtils';


export const useClinicalSessionState = ({
    sessionId,
    session,
    sessionData,
    user,
    userName,
    updateSessionData,
}) => {
    const { user: currentUser, typeOfUser } = useContext(AuthContext);
    const { data: preRegisteredUsers = [] } = usePreRegisteredUsers();

    const [teachersForStudent, setTeachersForStudent] = useState([]);
    const [isSavingSessionNotes, setIsSavingSessionNotes] = useState(false);

    // Refs to capture dataEntryIds from goal and quick track modals
    const goalsDataEntryRef = useRef({});
    const quickTracksDataEntryRef = useRef({});
    // Refs to GoalsDataCard and QuickTracksDataCard components for imperative save calls
    const goalsDataCardRef = useRef(null);
    const quickTracksDataCardRef = useRef(null);

    const normalizedUserSubRole = useMemo(
        () => getUserSubRoleName(currentUser),
        [currentUser]
    );

    const isEditingExistingSession = Boolean(
        sessionData?.editingSessionId || session?.originalData
    );

    const clinicalSessionAuthorId =
        session?.originalData?.ClinicalSessionAuthor?.ClinicianID ||
        sessionData?.clinicianId;
    const isCurrentUserClinicalAuthor =
        !!clinicalSessionAuthorId &&
        !!currentUser?.uid &&
        clinicalSessionAuthorId === currentUser.uid;

    const shouldLockSessionType =
        Boolean(normalizedUserSubRole) && isCurrentUserClinicalAuthor;

    // Initialize dataEntryIds from original session data
    useEffect(() => {
        const goals = session?.originalData?.Goals;
        if (!Array.isArray(goals) || goals.length === 0) return;
        goalsDataEntryRef.current = goalsDataEntryRef.current || {};
        goals.forEach(({ goalID, dataEntryID }) => {
            if (goalID && dataEntryID && !goalsDataEntryRef.current[goalID]) {
                goalsDataEntryRef.current[goalID] = dataEntryID;
            }
        });
    }, [session?.originalData?.Goals]);

    useEffect(() => {
        const quickTracks = session?.originalData?.QuickTracks;
        if (!Array.isArray(quickTracks) || quickTracks.length === 0) return;
        quickTracksDataEntryRef.current = quickTracksDataEntryRef.current || {};
        quickTracks.forEach(({ quickTrackID, dataEntryID }) => {
            if (
                quickTrackID &&
                dataEntryID &&
                !quickTracksDataEntryRef.current[quickTrackID]
            ) {
                quickTracksDataEntryRef.current[quickTrackID] = dataEntryID;
            }
        });
    }, [session?.originalData?.QuickTracks]);

    // Initialize lastSavedAt from original session data
    useEffect(() => {
        if (!sessionId || sessionData?.lastSavedAt) {
            return;
        }
        const derivedLastSaved =
            toIsoString(session?.originalData?.updatedAt) ||
            toIsoString(session?.originalData?.lastSavedAt);
        if (derivedLastSaved) {
            updateSessionData(sessionId, { lastSavedAt: derivedLastSaved });
        }
    }, [
        sessionId,
        sessionData?.lastSavedAt,
        session?.originalData?.updatedAt,
        session?.originalData?.lastSavedAt,
        updateSessionData,
    ]);

    // Initialize form fields in context on first render if they're empty
    useEffect(() => {
        if (user && userName && sessionId) {
            const initialData = {
                user,
                userName,
            };

            // Only set initial values if they haven't been set yet
            if (!sessionData.clinicianId) {
                initialData.clinicianId = currentUser?.uid;
                initialData.clinicianName =
                    currentUser?.displayName ||
                    currentUser?.name ||
                    currentUser?.fullname;
                initialData.clinicianNpi =
                    session?.originalData?.ClinicalSessionAuthor?.ClinicianNPI ||
                    currentUser?.provider?.npi ||
                    '';
            } else if (!sessionData.clinicianNpi) {
                initialData.clinicianNpi =
                    session?.originalData?.ClinicalSessionAuthor?.ClinicianNPI ||
                    currentUser?.provider?.npi ||
                    '';
            }
            if (!sessionData.studentId) {
                initialData.studentId = user?.studentId || user?.id;
                initialData.studentName =
                    user?.displayName || user?.name || user?.fullname;
            }
            if (!sessionData.date) {
                const today = new Date();
                const year = today.getFullYear();
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const day = String(today.getDate()).padStart(2, '0');
                initialData.date = `${year}-${month}-${day}`;
            }
            if (!sessionData.time) {
                initialData.time = dayjs();
            }

            updateSessionData(sessionId, initialData);
        }
    }, [sessionId, user, userName, currentUser, sessionData, session, updateSessionData]);

    // Auto-set session type based on user sub-role
    useEffect(() => {
        if (
            !sessionId ||
            !shouldLockSessionType ||
            !normalizedUserSubRole ||
            sessionData?.sessionType ||
            isEditingExistingSession
        ) {
            return;
        }

        updateSessionData(sessionId, {
            sessionType: normalizedUserSubRole,
            customSessionType: '',
        });
    }, [
        sessionId,
        shouldLockSessionType,
        normalizedUserSubRole,
        sessionData?.sessionType,
        isEditingExistingSession,
        updateSessionData,
    ]);

    // Fetch teachers for the student
    const fetchTeachersForStudent = useCallback(
        async (studentId) => {
            if (!studentId) return;

            try {
                const fetchedData = await fetchingTeachersForStudents(studentId);
                const filteredData = fetchedData.filter(
                    (item) => item.uid !== currentUser?.uid
                );
                setTeachersForStudent(filteredData);
            } catch (err) {
                console.log('Error fetching teachers for student:', err);
                setTeachersForStudent([]);
            }
        },
        [currentUser?.uid]
    );

    // Fetch teachers when student ID is available
    useEffect(() => {
        const studentId = sessionData.studentId || user?.studentId || user?.id;
        if (studentId) {
            fetchTeachersForStudent(studentId);
        }
    }, [
        sessionData.studentId,
        user?.studentId,
        user?.id,
        fetchTeachersForStudent,
    ]);

    // Check if user is pre-registered
    const isPreRegistered = useCallback(
        (id) => {
            return preRegisteredUsers.some((student) => student.id === id);
        },
        [preRegisteredUsers]
    );

    // Determine if session is a draft
    const isDraftSession = useMemo(() => {
        if (!session || !isEditingExistingSession || !session?.originalData) {
            return false;
        }

        const normalizeStatus = (value) => {
            if (typeof value !== 'string') return null;
            const trimmed = value.trim().toLowerCase();
            return trimmed || null;
        };

        const statusCandidates = [
            session?.originalData?.status,
            session?.originalData?.Status,
            session?.originalData?.sessionStatus,
        ];

        for (const candidate of statusCandidates) {
            const normalized = normalizeStatus(candidate);
            if (normalized) {
                return normalized === 'draft';
            }
        }

        if (session?.originalData?.isDraft === true) {
            return true;
        }

        const info = session?.originalData?.info;
        if (info) {
            let parsedInfo = null;
            if (typeof info === 'object' && !Array.isArray(info)) {
                parsedInfo = info;
            } else if (typeof info === 'string') {
                try {
                    parsedInfo = smartJsonAtob(info);
                } catch (error) {
                    console.warn('Failed to decode original session info:', error);
                }
            }

            if (parsedInfo) {
                const infoStatus = normalizeStatus(
                    parsedInfo?.status || parsedInfo?.Status || parsedInfo?.sessionStatus
                );

                if (infoStatus) {
                    return infoStatus === 'draft';
                }

                if (parsedInfo?.isDraft === true) {
                    return true;
                }
            }
        }

        return false;
    }, [isEditingExistingSession, session]);

    return {
        currentUser,
        typeOfUser,
        preRegisteredUsers,
        teachersForStudent,
        isSavingSessionNotes,
        setIsSavingSessionNotes,
        goalsDataEntryRef,
        quickTracksDataEntryRef,
        goalsDataCardRef,
        quickTracksDataCardRef,
        normalizedUserSubRole,
        isEditingExistingSession,
        isCurrentUserClinicalAuthor,
        shouldLockSessionType,
        isPreRegistered,
        isDraftSession,
    };
};

